/**
 * Client-side photo compression for note uploads (JC-111).
 *
 * Photos are re-encoded as lossy JPEG targeting a utilitarian budget
 * (~120KB) before upload: big camera shots (5-12MB) become fast to upload on
 * field connections and cheap to store, and HEIC input is normalised to JPEG.
 */

export const NOTE_PHOTO_MAX_BYTES = 120 * 1024
export const NOTE_PHOTO_MAX_DIMENSION = 1600

/** Region of the source image to keep, in natural (image) pixels. */
export interface CropRegion {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Produces an encoded blob for a given output scale (0-1 of the base target
 * dimensions) and JPEG quality (0-1), or null when encoding is unavailable.
 */
export type ImageEncoder = (
  scale: number,
  quality: number,
) => Promise<Blob | null>

export interface EncodingFit {
  blob: Blob
  scale: number
  quality: number
}

const SCALE_STEPS = [1, 0.75, 0.5, 0.35, 0.25]
const QUALITY_STEPS = [0.8, 0.65, 0.5]

/**
 * Search for the best encoding within the byte budget: prefer full scale and
 * high quality, degrade quality before dimensions. When nothing fits, return
 * the smallest attempt as a best effort rather than failing the upload.
 */
export async function findEncodingFit(
  encode: ImageEncoder,
  maxBytes: number,
): Promise<EncodingFit | null> {
  let smallest: EncodingFit | null = null

  for (const scale of SCALE_STEPS) {
    for (const quality of QUALITY_STEPS) {
      const blob = await encode(scale, quality)
      if (!blob) return smallest
      if (blob.size <= maxBytes) {
        return { blob, scale, quality }
      }
      if (!smallest || blob.size < smallest.blob.size) {
        smallest = { blob, scale, quality }
      }
    }
  }

  return smallest
}

export interface CompressedImage {
  file: File
  dataUrl: string
  width: number
  height: number
}

export interface CompressImageOptions {
  maxBytes?: number
  maxDimension?: number
  /** Region of the source to keep, in natural pixels. Defaults to the full image. */
  crop?: CropRegion
  fileName?: string
}

function loadImageElement(source: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to decode image'))
    }
    image.src = url
  })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality)
  })
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}

function jpegFileName(original?: string): string {
  const base = (original || 'photo').replace(/\.[^.]+$/, '')
  return `${base || 'photo'}.jpg`
}

/**
 * Rotate an image blob a quarter turn (direction 1 = clockwise, -1 =
 * counter-clockwise) at full resolution, re-encoded as high-quality JPEG.
 * Returns null when the source cannot be decoded or encoded in this browser
 * (e.g. HEIC on desktop Chrome) — callers should leave the image unchanged.
 */
export async function rotateImageQuarterTurn(
  source: Blob,
  direction: 1 | -1,
): Promise<CompressedImage | null> {
  let image: HTMLImageElement
  try {
    image = await loadImageElement(source)
  } catch {
    return null
  }

  const { naturalWidth, naturalHeight } = image
  if (!naturalWidth || !naturalHeight) return null

  const canvas = document.createElement('canvas')
  canvas.width = naturalHeight
  canvas.height = naturalWidth
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((direction * Math.PI) / 2)
  ctx.drawImage(image, -naturalWidth / 2, -naturalHeight / 2)

  // High quality: the save-time compression pass (findEncodingFit) is the
  // one that trades quality for the byte budget, not rotation.
  const blob = await canvasToBlob(canvas, 0.92)
  if (!blob) return null

  const file = new File([blob], jpegFileName((source as File).name), {
    type: 'image/jpeg',
  })
  const dataUrl = await blobToDataUrl(blob)
  return { file, dataUrl, width: canvas.width, height: canvas.height }
}

/**
 * Re-encode (and optionally crop) an image blob as JPEG within the byte
 * budget. Returns null when the source cannot be decoded or encoded in this
 * browser (e.g. HEIC on desktop Chrome) — callers should fall back to
 * uploading the original.
 */
export async function compressImageToJpeg(
  source: Blob,
  options?: CompressImageOptions,
): Promise<CompressedImage | null> {
  const maxBytes = options?.maxBytes ?? NOTE_PHOTO_MAX_BYTES
  const maxDimension = options?.maxDimension ?? NOTE_PHOTO_MAX_DIMENSION

  let image: HTMLImageElement
  try {
    image = await loadImageElement(source)
  } catch {
    return null
  }

  const naturalWidth = image.naturalWidth
  const naturalHeight = image.naturalHeight
  if (!naturalWidth || !naturalHeight) return null

  const region: CropRegion = options?.crop ?? {
    x: 0,
    y: 0,
    width: naturalWidth,
    height: naturalHeight,
  }
  if (region.width < 1 || region.height < 1) return null

  const baseScale = Math.min(
    1,
    maxDimension / Math.max(region.width, region.height),
  )

  const encode: ImageEncoder = async (scale, quality) => {
    const width = Math.max(1, Math.round(region.width * baseScale * scale))
    const height = Math.max(1, Math.round(region.height * baseScale * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(
      image,
      region.x,
      region.y,
      region.width,
      region.height,
      0,
      0,
      width,
      height,
    )
    return canvasToBlob(canvas, quality)
  }

  const fit = await findEncodingFit(encode, maxBytes)
  if (!fit) return null

  const file = new File([fit.blob], jpegFileName((source as File).name), {
    type: 'image/jpeg',
  })
  const dataUrl = await blobToDataUrl(fit.blob)
  const width = Math.max(1, Math.round(region.width * baseScale * fit.scale))
  const height = Math.max(1, Math.round(region.height * baseScale * fit.scale))

  return { file, dataUrl, width, height }
}
